import { useEffect, useState, useCallback, useRef } from "react";
import useAuth from "../context/AuthProvider";
import { YearToDateReportModel, YearToDateReportModel_Resp } from "../models/YearToDateReportModel";
import { Box, Button, LinearProgress, TextField, FormControl } from "@mui/material";
import { DataGridPro, GridColDef, GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { apiClient } from "../helper/api";
import moment from "moment";
import { RiExpandUpDownLine } from "react-icons/ri";
import Swal from "sweetalert2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { getRowsPerPageOptions } from "../components/AddEdit/CustomFunctions";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { writeXlsxFile, formatUtcToLocal } from "../helper/utils";
import { Close, FileDownload } from "@mui/icons-material";

export const YearToDateReport = () => {
  const { user } = useAuth();
  const token = user?.token;
  const [loading, setLoading] = useState(false);
  
  // Ref to hold AbortController for cancelling pending requests
  const controllerRef = useRef<AbortController | null>(null);
  
  const initValue: YearToDateReportModel_Resp = {
    items: [],
    totalCount: 0,
    pageSize: 25,
    pageCount: 0,
    currentPage: 0,
  };

  const [data, setData] = useState<YearToDateReportModel[]>(initValue.items);
  const [totalCount, setTotalCount] = useState(initValue.totalCount);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const fetchData = useCallback(async () => {
    // CANCEL previous pending request
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    // Create a new controller for this request
    const currentController = new AbortController();
    controllerRef.current = currentController;
    const signal = currentController.signal;

    setLoading(true);
    
    try {
      const sortField = sortModel.length > 0 ? sortModel[0].field : 'dateFiled';
      const sortDirection = sortModel.length > 0 ? sortModel[0].sort || 'desc' : 'desc';
      
      const requestBody: any = {
        pageNumber: currentPage + 1,
        pageSize: pageSize,
        field: sortField,
        sort: sortDirection,
        filterModel: filterModel,
      };
      if (startDate) {
        requestBody.startDate = startDate.format('YYYY-MM-DD');
      }
      if (endDate) {
        requestBody.endDate = endDate.format('YYYY-MM-DD');
      }

      const res = await apiClient(`/YearToDateReport/getYearToDateReport`, {
        method: "POST",
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }),
        body: JSON.stringify(requestBody),
        signal, // IMPORTANT: Pass abort signal
      });
      
      // If request was aborted, do not update state
      if (!res) {
        return;
      }
      
      if (res) {
        // API returns: { data: Array, pagination: { totalRecords, currentPage, pageSize, totalPages } }
        // Note: toCamel converts PascalCase to camelCase (RecordId -> recordId, Client -> client, etc.)
        const responseData = res.data || [];
        const totalRecords = res.totalRecords || 0;
        const currentPage = res.currentPage;
        
        // Set currentPage from API response if available
        // Convert from 1-based (API) to 0-based (DataGrid) if needed
        if (currentPage !== undefined && currentPage !== null) {
          // API typically uses 1-based pagination, DataGrid uses 0-based
          const dataGridPage = currentPage > 0 ? currentPage - 1 : 0;
          setCurrentPage(dataGridPage);
        }
        
        // Ensure data is an array
        if (Array.isArray(responseData) && responseData.length > 0) {
          setData(responseData);
          setTotalCount(totalRecords);
        } else {
          setData([]);
          setTotalCount(0);
        }
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      // If request was aborted, do not show error or update state
      if (error.name === "AbortError") {
        return;
      }
      console.error("Error fetching Year to Date Report data:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load Year to Date Report data',
        confirmButtonText: 'OK'
      });
    } finally {
      if (controllerRef.current === currentController) {
        setLoading(false);
      }
    }
  }, [currentPage, pageSize, filterModel, sortModel, startDate, endDate, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup: Cancel any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };
  }, []);

  const handleFilterModelChange = useCallback((newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
    setCurrentPage(0);
  }, []);

  const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    setCurrentPage(0);
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'client',
      type: 'text',
      headerName: 'Client',
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'client'
          );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
        <div>
            <span className="table-card-title" >{params.colDef.headerName}</span>
            {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )},
    },
    {
      field: 'state',
      type: 'text',
      headerName: 'State',
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      align: 'left',
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'state'
          );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
        <div>
            <span className="table-card-title" >{params.colDef.headerName}</span>
            {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )},
    },
    {
      field: 'invoiceNumber',
      type: 'text',
      headerName: 'Invoice Number',
      flex: 1,
      minWidth: 160,
      headerAlign: 'left',
      align: 'left',
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'invoiceNumber'
          );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
        <div>
            <span className="table-card-title" >{params.colDef.headerName}</span>
            {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )},
    },
    {
      field: 'insuredName',
      type: 'text',
      headerName: 'Insured Name',
      flex: 1,
      minWidth: 180,
      headerAlign: 'left',
      align: 'left',
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'state'
          );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
        <div>
            <span className="table-card-title" >{params.colDef.headerName}</span>
            {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )},
    },
    {
      field: 'policyNumber',
      type: 'text',
      headerName: 'Policy Number',
      flex: 1,
      minWidth: 160,
      headerAlign: 'left',
      align: 'left',
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'insuredName'
          );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
        <div>
            <span className="table-card-title" >{params.colDef.headerName}</span>
            {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )},
    },
    {
      field: 'dateFiled',
      type: 'date',
      headerName: 'Date Filed',
      flex: 1,
      minWidth: 140,
      headerAlign: 'left',
      align: 'left',
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'policyNumber'
          );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
        <div>
            <span className="table-card-title" >{params.colDef.headerName}</span>
            {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )},
      valueGetter: (params) => {
        if (!params.value) return null;
        return new Date(params.value);
      },
      renderCell: (params) => (
        <div>
          {params.value ? formatUtcToLocal(params.value, "MM/DD/YYYY") : ""}
        </div>
      ),
    },
    {
      field: 'transactionType',
      type: 'text',
      headerName: 'Transaction Type',
      flex: 1,
      minWidth: 180,
      headerAlign: 'left',
      align: 'left',
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'transactionType'
          );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
        <div>
            <span className="table-card-title" >{params.colDef.headerName}</span>
            {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )},
      renderCell: (params) => (
        <div>
          {params.value || ''}
        </div>
      ),
    },
    {
      field: 'reportName',
      type: 'text',
      headerName: 'Report Name',
      flex: 2,
      minWidth: 250,
      headerAlign: 'left',
      align: 'left',
      hideSortIcons: true,
      renderHeader: (params) => {
        const sortItem = sortModel.find(
            (item: any) => item.field === 'reportName'
          );
      
          const isSortedAsc = sortItem?.sort === 'asc';
          const isSortedDesc = sortItem?.sort === 'desc'
          return (
        <div>
            <span className="table-card-title" >{params.colDef.headerName}</span>
            {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
        </div>
      )},
      renderCell: (params) => (
        <div style={{ 
          whiteSpace: 'normal', 
          wordWrap: 'break-word', 
          overflow: 'visible',
          textAlign: 'left',
          width: '100%'
        }}>
          {params.value || ''}
        </div>
      ),
    },
  ];

  const handleStartDateChange = (newValue: unknown) => {
    setStartDate(newValue as Dayjs | null);
    setCurrentPage(0);
  };

  const handleEndDateChange = (newValue: unknown) => {
    setEndDate(newValue as Dayjs | null);
    setCurrentPage(0);
  };

  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(0);
  };

  const fetchAllDataForExport = async () => {
    const sortField = sortModel.length > 0 ? sortModel[0].field : 'dateFiled';
    const sortDirection = sortModel.length > 0 ? sortModel[0].sort || 'desc' : 'desc';
    
    // Use totalCount if available, otherwise use a large number to fetch all records
    const pageSizeForExport = totalCount > 0 ? totalCount : 100000;
    
    const requestBody: any = {
      pageNumber: 1,
      pageSize: pageSizeForExport,
      field: sortField,
      sort: sortDirection,
      filterModel: filterModel,
    };
    if (startDate) {
      requestBody.startDate = startDate.format('YYYY-MM-DD');
    }
    if (endDate) {
      requestBody.endDate = endDate.format('YYYY-MM-DD');
    }

    const res = await apiClient(`/YearToDateReport/getYearToDateReport`, {
      method: "POST",
      headers: new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }),
      body: JSON.stringify(requestBody)
    });

    if (res && res.data) {
      return res.data || [];
    }
    return [];
  };

  const handleExportXlsx = async () => {
    try {
      setLoading(true);
      const allData = await fetchAllDataForExport();
      
      if (!allData || allData.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'No Data',
          text: 'No data available to export',
          confirmButtonText: 'OK'
        });
        setLoading(false);
        return;
      }

      const fieldsToExport: (keyof YearToDateReportModel)[] = [
        'client', 'state', 'invoiceNumber', 'insuredName', 
        'policyNumber', 'dateFiled', 'transactionType', 'reportName'
      ];

      const capitalizeFirstLetter = (str: string) => {
        return str.charAt(0).toUpperCase() + str.slice(1).replace(/([A-Z])/g, ' $1').trim();
      };

      const formattedValues = allData.map((item: YearToDateReportModel) => {
        return fieldsToExport.map((key: keyof YearToDateReportModel) => {
          const value = item[key];
          if (key === 'dateFiled' && value) {
            return formatUtcToLocal(value, "MM/DD/YYYY");
          }
          return value || '';
        });
      });

      const headers = fieldsToExport.map(field => capitalizeFirstLetter(field));
      // Add title row
      const titleRow = new Array(fieldsToExport.length).fill('');
      titleRow[0] = 'Year to Date Report';
      
      // Add summary row for transaction total
      const summaryRow = new Array(fieldsToExport.length).fill('');
      summaryRow[0] = 'Transaction Total';
      summaryRow[1] = allData.length.toLocaleString();

      const exportData = [
        titleRow,
        summaryRow, 
        [],
        headers,
        ...formattedValues 
      ];

      writeXlsxFile(exportData, "YearToDateReport");
    } catch (error) {
      console.error("Error exporting data:", error);
      Swal.fire({
        icon: 'error',
        title: 'Export Error',
        text: 'Failed to export data',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const datePickerSx = {
    width: '200px',
    '& .MuiOutlinedInput-root': {
      height: '36px',
      '& fieldset': { borderColor: '#e2e8f0', borderRadius: '6px' },
      '&:hover fieldset': { borderColor: '#2099d2' },
      '&.Mui-focused fieldset': { borderColor: '#2099d2', borderWidth: '1px' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#2099d2' },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
        {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
      </div>
      <div style={{ height: 'calc(100vh - 155px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.5,
            px: { xs: 4, md: 2 },
            py: 1.5,
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#fafbfc',
            minHeight: 70,
            flexWrap: 'wrap',
            mb: 3,
            marginLeft: { xs: '-24px', md: '0px' },
            marginRight: { xs: '-24px', md: '0px' },
          }}
        >
          <h4 className="mb-0" style={{ fontWeight: 600, color: '#0f172a' }}>
            Year to Date Report
          </h4>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small">
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                inputFormat="MM/DD/YYYY"
                maxDate={endDate || undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Start Date"
                    size="small"
                    sx={datePickerSx}
                  />
                )}
              />
            </FormControl>
            <FormControl size="small">
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                inputFormat="MM/DD/YYYY"
                minDate={startDate || undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="End Date"
                    size="small"
                    sx={datePickerSx}
                  />
                )}
              />
            </FormControl>
            {(startDate || endDate) && (
              <Button
                variant="outlined"
                size="medium"
                onClick={handleClearDates}
                startIcon={<Close style={{ fontSize: '16px' }} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#e2e8f0',
                  color: '#475569',
                  height: '36px',
                  marginTop: '2px',
                  '&:hover': {
                    borderColor: '#2099d2',
                    backgroundColor: '#2099d214',
                    color: '#2099d2',
                  },
                }}
              >
                Clear
              </Button>
            )}
            {(user?.userRoles.split(",").includes("Super Admin") ||
              user?.userRoles.split(",").includes("Customer Admin")) && (
              <Button
                variant="contained"
                size="medium"
                startIcon={<FileDownload style={{ fontSize: '16px' }} />}
                onClick={handleExportXlsx}
                title="Export"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#2099d2',
                  height: '36px',
                  marginTop: '2px',
                  '&:hover': { backgroundColor: '#176990' },
                }}
              >
                Export
              </Button>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)',
            border: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            position: 'relative',
            mx: 0,
          }}
        >
          {loading && (<>
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                  backgroundColor: "rgba(250, 251, 252, 0.85)",
                  zIndex: 10,
                  border: "1px solid #e2e8f0",
                  borderRadius: 1,
              }}
          >
              <Box sx={{ width: "100%", maxWidth: 320 }}>
                  <LinearProgress color="info" sx={{ backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#132D3D" } }} />
                  <Box sx={{ mt: 1.5, textAlign: "center" }}>
                      <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>Loading...</span>
                  </Box>
              </Box>
          </Box>
          </>)}
          <DataGridPro
            rows={data || []}
            columns={columns}
            pagination
            page={currentPage}
            pageSize={pageSize}
            rowCount={totalCount}
            rowsPerPageOptions={getRowsPerPageOptions(totalCount, pageSize)}
            paginationMode="server"
            filterMode="server"
            sortingMode="server"
            sortingOrder={['asc', 'desc']}
            onPageChange={(newPage) => setCurrentPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }}
            onFilterModelChange={handleFilterModelChange}
            onSortModelChange={handleSortModelChange}
            filterModel={filterModel}
            sortModel={sortModel}
            disableSelectionOnClick
            style={{ textAlign: "center" }}
            getRowId={(row) => {
              const id = row.recordId || row.id || `${row.recordType || 'record'}-${row.invoiceNumber || row.reportName || ''}-${row.policyNumber || ''}-${row.client || ''}-${row.state || ''}`;
              return id;
            }}
            sx={{
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              border: 'none',
              fontSize: '14px',
              color: '#475569',
              backgroundColor: '#ffffff',
              '& .MuiDataGridPro-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: 1.35,
                fontWeight: 600,
                fontSize: '12px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: '#64748b',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                borderBottom: '2px solid #e2e8f0',
                // borderTop: '2px solid #e2e8f0',
                minHeight: '52px !important',
                maxHeight: '52px !important',
              },
              '& .MuiDataGrid-columnHeader': {
                '&:focus, &:focus-within': { outline: 'none' },
              },
              '& .MuiDataGrid-row': {
                transition: 'background-color 0.15s ease',
                '&:nth-of-type(even)': {
                  backgroundColor: '#fafbfc',
                },
                '&:hover': {
                  backgroundColor: '#f1f5f9 !important',
                },
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f1f5f9',
                fontSize: '14px',
                color: '#334155',
                overflow: 'visible',
                whiteSpace: 'normal',
                lineHeight: 'normal',
                wordWrap: 'break-word',
                textOverflow: 'clip',
                padding: '8px 16px',
                '&:focus': { outline: 'none' },
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                marginBottom: '0px !important',
              },
              '& .MuiDataGrid-virtualScroller': {
                border: 'none',
              },
              '& .MuiDataGrid-main': {
                border: 'none',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                minHeight: 56,
              },
              '& .MuiTablePagination-root': {
                color: '#64748b',
                fontSize: '13px',
              },
              '& .MuiTablePagination-select': {
                fontSize: '13px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
              },
              '& .MuiIconButton-root': {
                color: '#64748b',
                '&:hover': {
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                },
              },
            }}
          />
        </Box>
      </div>
    </LocalizationProvider>
  );
};

